import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeftRight,
  Banknote,
  Coins,
  Gift,
  LayoutDashboard,
  Search,
  Settings,
  Users,
  Wallet,
} from 'lucide-react';
import { gauntlet } from '../lib/api';
import { cx } from '../lib/format';

interface Command {
  id: string;
  label: string;
  hint?: string;
  Icon: typeof Search;
  run: () => void;
}

/**
 * Ctrl-K / Cmd-K search.
 *
 * Two things at once: a jump-to-page palette, and a full-text search over the
 * FTS5 index the server already maintains for labels, contacts and
 * transactions. That index existed with nothing querying it.
 */
export function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((v) => !v);
      }
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery('');
      setCursor(0);
      // The modal mounts in the same tick, so focus has to wait a frame.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  /* Only hit the server once the query looks deliberate. FTS5 on a two-letter
     prefix returns most of the table and none of it is useful. */
  const { data: searchData } = useQuery({
    queryKey: ['search', query],
    queryFn: () => gauntlet.search(query),
    enabled: open && query.trim().length >= 2,
    staleTime: 10_000,
  });

  const pages: Command[] = useMemo(
    () => [
      { id: 'p-dash', label: 'Dashboard', Icon: LayoutDashboard, run: () => void navigate('/') },
      { id: 'p-act', label: 'Activity', hint: 'sent and received', Icon: ArrowLeftRight, run: () => void navigate('/activity') },
      { id: 'p-fam', label: 'Family', hint: 'profiles and limits', Icon: Users, run: () => void navigate('/family') },
      { id: 'p-funds', label: 'Add funds', Icon: Banknote, run: () => void navigate('/add-funds') },
      { id: 'p-spend', label: 'Spend', hint: 'gift cards, eSIMs, topups', Icon: Gift, run: () => void navigate('/spend') },
      { id: 'p-chain', label: 'Chains', hint: 'add a network', Icon: Coins, run: () => void navigate('/chains') },
      { id: 'p-set', label: 'Settings', Icon: Settings, run: () => void navigate('/settings') },
      { id: 'a-sync', label: 'Sync now', hint: 'check every chain', Icon: Wallet, run: () => void gauntlet.sync() },
    ],
    [navigate],
  );

  const needle = query.trim().toLowerCase();
  const matchedPages = needle
    ? pages.filter(
        (c) => c.label.toLowerCase().includes(needle) || c.hint?.toLowerCase().includes(needle),
      )
    : pages;

  const results: Command[] = [
    ...matchedPages,
    ...(searchData?.results ?? []).slice(0, 8).map((row, i) => ({
      id: `s-${i}`,
      label: row.body.slice(0, 70),
      hint: row.kind,
      Icon: Search,
      run: () => {
        // The index stores a kind and a reference; transactions are the only
        // kind with somewhere useful to land right now.
        if (row.kind === 'transaction') void navigate('/activity');
        else void navigate('/');
      },
    })),
  ];

  const choose = (command: Command | undefined): void => {
    if (!command) return;
    command.run();
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 p-4 pt-[12vh] backdrop-blur-sm"
      onClick={() => setOpen(false)}
      role="presentation"
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-xl border border-hairline bg-raised shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Search"
      >
        <div className="flex items-center gap-2.5 border-b border-hairline px-4">
          <Search size={16} className="shrink-0 text-ink-faint" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setCursor(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setCursor((c) => Math.min(c + 1, results.length - 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setCursor((c) => Math.max(c - 1, 0));
              } else if (e.key === 'Enter') {
                e.preventDefault();
                choose(results[cursor]);
              }
            }}
            placeholder="Go to a page, or search labels, contacts and transactions…"
            className="h-12 w-full bg-transparent text-sm text-ink outline-none placeholder:text-ink-faint"
          />
          <kbd className="shrink-0 rounded border border-hairline px-1.5 py-0.5 text-[10px] text-ink-faint">
            esc
          </kbd>
        </div>

        <ul className="max-h-80 overflow-y-auto py-1">
          {results.map((command, index) => (
            <li key={command.id}>
              <button
                onMouseEnter={() => setCursor(index)}
                onClick={() => choose(command)}
                className={cx(
                  'flex w-full items-center gap-3 px-4 py-2 text-left transition-colors',
                  index === cursor ? 'bg-brass-wash text-brass' : 'text-ink hover:bg-abyss',
                )}
              >
                <command.Icon size={15} className="shrink-0 opacity-70" />
                <span className="min-w-0 flex-1 truncate text-[13px]">{command.label}</span>
                {command.hint && (
                  <span className="shrink-0 text-[11px] text-ink-faint">{command.hint}</span>
                )}
              </button>
            </li>
          ))}
          {results.length === 0 && (
            <li className="px-4 py-6 text-center text-sm text-ink-faint">Nothing matches.</li>
          )}
        </ul>
      </div>
    </div>
  );
}
